'use client';

import { POLICY_URL, PRIVACY_URL } from '@diritto/common/constants/url';
import { Color } from '@diritto/common/style/color';
import { useQueryClient } from '@tanstack/react-query';
import { AccountApi } from 'api/ApiHelper';
import axios, { AxiosError } from 'axios';
import { QueryKey } from 'constants/reactQuery';
import { ArrowIcon } from 'icons/ArrowIcon';
import { CheckIcon } from 'icons/CheckIcon';
import { DirittoAppBridge } from 'libs/DirittoAppBridge';
import { DateTime, Duration } from 'luxon';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { fetchAccountMeQuery } from 'queries/account/useGetAccountMeQuery';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { CheckBox } from 'ui-components/common/CheckBox';
import { useClient } from 'ui-hooks/useClient';
import { showToast } from 'utils/modal';

export default function PartnerSignUpForm() {
  const tokenInputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const queryClient = useQueryClient();
  const client = useClient();

  const [tokenState, setTokenState] = useState({
    challengeId: '',
    expiresAt: 0,
    error: '',
    success: false,
  });

  const [stateVerify, setStateVerify] = useState({
    challengeId: '',
    expiresAt: 0,
    error: '',
    success: false,
  });

  const [signupError, setSignupError] = useState('');
  const [nameError, setNameError] = useState('');

  const [agreePartner, setAgreePartner] = useState(false);
  const [agreePersonalInfo, setAgreePersonalInfo] = useState(false);
  const [tokenRemainingTime, setTokenRemainingTime] = useState('00:00');

  const [name, setName] = useState('');

  const [email, setEmail] = useState('');
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [hasFocus, setHasFocus] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const [token, setToken] = useState('');
  const [shouldShowTokenInput, setShouldShowTokenInput] = useState(false);
  const [isTokenExpired, setIsTokenExpired] = useState(false);

  const agreeAll = agreePartner && agreePersonalInfo;

  const handleSendToken = useCallback(async () => {
    try {
      const res = await AccountApi.sendSignUpVerificationToken({
        email,
      });
      if (res.challengeId) {
        setTokenState({
          challengeId: res.challengeId,
          expiresAt: res.expiresAt ?? 0,
          error: '',
          success: true,
        });
        setShouldShowTokenInput(true);
        showToast('일회용 코드를 등록된 메일로 보냈어요.');
      } else {
        showToast('일회용 코드를 보내는데 실패했어요.');
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const data = (
          e as AxiosError<{
            message?: string;
          }>
        ).response?.data;

        setTokenState({
          challengeId: '',
          expiresAt: 0,
          error: data?.message ?? '일회용 코드를 보내는데 실패했어요.',
          success: false,
        });
      }
    }
  }, [email]);

  const handleVerifyToken = useCallback(async () => {
    try {
      await AccountApi.verifyEmailVerificationToken({
        challengeId: tokenState.challengeId,
        token,
      });

      setStateVerify({
        challengeId: tokenState.challengeId,
        expiresAt: tokenState.expiresAt,
        error: '',
        success: true,
      });

      showToast('이메일 인증이 완료되었어요.');
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const data = (
          e as AxiosError<{
            message?: string;
          }>
        ).response?.data;

        setStateVerify({
          challengeId: '',
          expiresAt: 0,
          error: data?.message ?? '일회용 코드 검증에 실패했어요.',
          success: false,
        });
      }
    }
  }, [token, tokenState.challengeId, tokenState.expiresAt]);

  const handleSignup = useCallback(async () => {
    if (!agreeAll) {
      showToast('약관에 동의해주세요.');
      return;
    }

    try {
      const response = await AccountApi.createPartnerAccount({
        challengeId: tokenState.challengeId,
        name,
        email,
      });

      await queryClient.invalidateQueries({ queryKey: [QueryKey.User], refetchType: 'all' });

      if (client.isWeb) {
        await fetchAccountMeQuery(queryClient);
        router.replace('/');
      } else {
        DirittoAppBridge.completeSign(response.token);
      }

      showToast('회원가입이 완료되었어요.');
      router.push('/accounts/partners/sign-up/verify-business-license');
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const data = (
          e as AxiosError<{
            message?: string;
          }>
        ).response?.data;

        if (data?.message === '이미 가입된 회사입니다.') {
          setNameError(data?.message);
          return;
        }

        setSignupError(data?.message ?? '회원가입에 실패했어요.');
      }
    }
  }, [agreeAll, email, name, tokenState.challengeId, router, queryClient, client]);

  useEffect(() => {
    if (shouldShowTokenInput) {
      tokenInputRef.current?.focus();
    }
  }, [shouldShowTokenInput]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (stateVerify.success) {
        clearInterval(interval);
        return;
      }

      const now = DateTime.now().toMillis();
      if (tokenState.expiresAt && tokenState.expiresAt < now && !stateVerify.success) {
        setIsTokenExpired(true);
        setStateVerify({
          challengeId: '',
          expiresAt: 0,
          error: '',
          success: false,
        });
        setTokenRemainingTime('00:00');
        clearInterval(interval);
        return;
      }

      setIsTokenExpired(false);
      setTokenRemainingTime(
        tokenState.expiresAt ? Duration.fromMillis(tokenState.expiresAt - now).toFormat('mm:ss') : '05:00',
      );
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [tokenState.expiresAt, stateVerify.success]);

  // 이메일 인증이 완료되었는데, 이메일 필드의 값을 사용자가 수정한 경우 이메일 인증을 다시 받아야 함
  const handleChangeEmail = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      setShouldShowTokenInput(false);
      setToken('');
      setTokenState({
        challengeId: '',
        expiresAt: 0,
        error: '',
        success: false,
      });
      stateVerify.success = false;
    },
    [stateVerify],
  );

  useEffect(() => {
    const handleFocus = () => setHasFocus(true);
    const handleBlur = () => setHasFocus(false);

    const input = emailInputRef.current;
    input?.addEventListener('focus', handleFocus);
    input?.addEventListener('blur', handleBlur);

    return () => {
      input?.removeEventListener('focus', handleFocus);
      input?.removeEventListener('blur', handleBlur);
    };
  }, []);

  return (
    <div className="container-center md:w-[440px] md:mt-[80px]">
      <div className="container gap-2 items-center">
        <div className="text-xl font-bold p">계정 만들기</div>
        <span className="text-gray-700">파트너 활동을 위한 로그인 계정을 생성해주세요.</span>
      </div>
      <div className="container gap-y-12">
        <div className="container gap-y-2">
          <div>
            <div className="container gap-9">
              <div className="container gap-2">
                <div className="flex justify-between items-center">
                  <label htmlFor="name" className="text-gray-600 text-xxs">
                    회사명 (필수)
                  </label>
                  <span className={`text-xxxs ${name.length > 16 ? 'text-red-500' : 'text-gray-500'}`}>
                    {name.length}/16
                  </span>
                </div>
                <input
                  type="search"
                  name="name"
                  value={name}
                  onChange={e => {
                    setName(e.target.value);
                    setNameError('');
                  }}
                  placeholder="디리토"
                  maxLength={16}
                  className={`input ${(nameError || name.length > 16) && 'border-red-500'}`}
                />
                <div className="flex flex-col gap-[2px]">
                  <span className="text-xxxs text-gray-500">법인사업자의 경우 법인명으로 입력해 주세요.</span>
                  {nameError && <p className="text-red-500 text-xxxs">{nameError}</p>}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="text-gray-600 text-xxs">
                  이메일 주소 (필수)
                </label>
                <div>
                  <div className="flex grow justify-center items-center gap-2.5">
                    <input
                      disabled={stateVerify.success}
                      ref={emailInputRef}
                      onMouseEnter={() => setIsHovered(true)}
                      onMouseLeave={() => setIsHovered(false)}
                      type="search"
                      name="email"
                      value={email}
                      onChange={handleChangeEmail}
                      placeholder="예) ritto@diritto.co.kr"
                      className={`input ${tokenState?.error && 'border-red-500'}`}
                    />
                    {stateVerify.success && shouldShowTokenInput && !hasFocus && !isHovered && (
                      <div className="-ml-[44px] pr-4">
                        <CheckIcon size={18} color={Color.Primary500} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-[2px]">
                  <span className="text-xxxs text-gray-500">기업 이메일로만 가입이 가능해요.</span>
                  {tokenState?.error && <p className="text-red-500 text-xxxs">{tokenState.error}</p>}
                </div>
                {email && (
                  <button
                    type="submit"
                    disabled={shouldShowTokenInput}
                    className={`h-12 px-4 pt-3.5 pb-[13px] text-xxs bg-white rounded border border-solid border-violet-500 justify-center items-center gap-2 inline-flex text-center text-violet-500 font-bold leading-[21px] ${
                      shouldShowTokenInput && 'opacity-30'
                    }`}
                    onClick={handleSendToken}>
                    이메일 인증하기
                  </button>
                )}
              </div>
            </div>
          </div>
          {shouldShowTokenInput && !stateVerify.success && (
            <div className="rounded bg-gray-100 p-4 flex flex-col gap-2">
              <div className="flex flex-col gap-2">
                <input type="hidden" value={tokenState.challengeId} name="challengeId" />
                <label htmlFor="token" className="text-gray-700 text-xxs">
                  이메일로 받은 인증코드를 입력해주세요.
                </label>

                <div className="flex grow items-center">
                  <input
                    type="number2"
                    name="token"
                    value={token}
                    onChange={e => setToken(e.target.value)}
                    placeholder="인증코드 6자리"
                    maxLength={6}
                    ref={tokenInputRef}
                    disabled={stateVerify.success}
                    className={`input  ${(isTokenExpired || stateVerify?.error) && 'border-red-500'}`}
                  />
                  <div className="-ml-[100px] mr-2 justify-between items-center inline-flex gap-x-2">
                    {!stateVerify.success && (
                      <div className="text-right text-primary-500 text-xxs font-medium tabular-nums">
                        {tokenState.expiresAt ? <p>{tokenRemainingTime}</p> : <p className="text-transparent">00:00</p>}
                      </div>
                    )}
                    <button
                      type="submit"
                      className="btn-primary text-xxxs px-3 py-1.5"
                      disabled={token.length !== 6 || stateVerify.success || isTokenExpired}
                      onClick={handleVerifyToken}>
                      {stateVerify.success ? '인증 완료' : '확인'}
                    </button>
                  </div>
                </div>
                {stateVerify.error && <p className="text-red-500 text-xxxs">{stateVerify.error}</p>}
              </div>
              {isTokenExpired && (
                <p className="text-red-500 text-xxxs">인증시간이 지났습니다. 인증코드를 재전송해주세요.</p>
              )}
              <div>
                <input type="hidden" name="email" value={email} />
                {!stateVerify.success && (
                  <p className="text-gray-600 text-xxxs">
                    이메일을 받지 못하셨나요?{' '}
                    <input
                      type="submit"
                      className="text-gray-500 underline border-none outline-none bg-transparent cursor-pointer"
                      value="인증코드 재전송"
                      onClick={handleSendToken}
                    />
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col">
          <div>
            <input type="hidden" name="name" value={name} />
            <input type="hidden" name="email" value={email} />
            <input type="hidden" name="challengeId" value={tokenState.challengeId} />
            <input type="hidden" name="token" value={token} />
            <div className="flex flex-col gap-4">
              <div className="flex flex-row gap-2 items-center">
                <CheckBox
                  checked={agreeAll}
                  onChange={() => {
                    setAgreePartner(!agreeAll);
                    setAgreePersonalInfo(!agreeAll);
                  }}>
                  <label htmlFor="agreeAll" className="text-xs font-bold">
                    전체 약관 동의
                  </label>
                </CheckBox>
              </div>
              <div className="border-[0.5px] border-solid border-gray-300 " />
              <div>
                <div className="container-row gap-2">
                  <CheckBox checked={agreePartner} onChange={() => setAgreePartner(!agreePartner)}>
                    <label htmlFor="agreePartner" className="text-xxs text-black">
                      [필수] 이용약관 동의
                    </label>
                  </CheckBox>
                </div>

                <div className="container-row gap-1 ml-3">
                  <Link href={POLICY_URL} className="text-xxs indent-7 text-gray-500" target="_blank">
                    약관 보기
                  </Link>
                  <ArrowIcon direction="right" className="size-[14px]" color={Color.Gray500} />
                </div>
              </div>
              <div>
                <div className="container-row gap-2">
                  <CheckBox checked={agreePersonalInfo} onChange={() => setAgreePersonalInfo(!agreePersonalInfo)}>
                    <label htmlFor="agreePersonalInfo" className="text-xxs text-black">
                      [필수] 개인정보 수집 및 이용 동의
                    </label>
                  </CheckBox>
                </div>
                <div className="container-row gap-1 items-center ml-3">
                  <Link href={PRIVACY_URL} className="text-xxs indent-7 text-gray-500" target="_blank">
                    개인정보 처리방침 보기
                  </Link>
                  <ArrowIcon direction="right" className="size-[14px]" color={Color.Gray500} />
                </div>
              </div>
            </div>
            <div className="container gap-2 pt-[40px] md:pt-[60px]">
              <button
                type="submit"
                className="btn btn-primary text-xs"
                disabled={!agreeAll || !name || !email || !token || !stateVerify.success}
                onClick={handleSignup}>
                가입하기
              </button>
              {signupError && <p className="text-red-500">{signupError}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
